import { useId, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import { cn } from '@/shared/lib/utils'

interface SidebarTooltipProps {
  label: string
  children: ReactNode
  className?: string
}

/** Right-hand label for icon-only controls of the collapsed sidebar. */
export function SidebarTooltip({
  label,
  children,
  className,
}: SidebarTooltipProps) {
  const tooltipId = useId()
  const [position, setPosition] = useState<{
    left: number
    top: number
  } | null>(null)

  const show = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect()
    setPosition({
      left: rect.right + 8,
      top: rect.top + rect.height / 2,
    })
  }

  return (
    <div
      className={cn('mx-auto size-9', className)}
      onPointerEnter={(event) => show(event.currentTarget)}
      onPointerLeave={() => setPosition(null)}
      onFocusCapture={(event) => show(event.currentTarget)}
      onBlurCapture={() => setPosition(null)}
      aria-describedby={position ? tooltipId : undefined}
    >
      {children}
      {position && typeof document !== 'undefined'
        ? createPortal(
            <div
              id={tooltipId}
              role="tooltip"
              className="pointer-events-none fixed z-[100] max-w-64 -translate-y-1/2 rounded-md border bg-popover px-2.5 py-1.5 text-xs font-medium text-popover-foreground shadow-md"
              style={{ left: position.left, top: position.top }}
            >
              {label}
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}
